import { getApolloClient } from "src/clients/apollo-client";
import { BULK_EXECUTE_AUTHGARD_ADMIN_OPERATION_MUTATION } from "src/common/gql-operations";
import { AuthgardAdminAction, TenantID } from "src/common/enums";
import {
    AdminOperationSuccess,
    AdminOperationTargetInput,
    AuthgardCredentialType,
    BulkExecuteAuthgardAdminOperationData,
    BulkExecuteAuthgardAdminOperationVariables,
} from "src/models/bulk-execute-authgard-admin-operation";
import { AuthgardItemResult, AuthgardResultStatus, partitionAuthgardResults } from "src/models/authgard-item-result";
import { MAX_AUTHGARD_ADMIN_TARGETS_PER_CALL } from "src/common/constants";
import { logger } from "src/logger";

import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [new KatalMetrics.Metric.String('provider', 'authgard-admin-provider')];
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);


export interface ExecuteAuthgardAdminOperationInput {
    scope: string;
    operation: AuthgardAdminAction;
    credentialType: AuthgardCredentialType;
    targets: AdminOperationTargetInput[];
}

/**
 * Runs one Authgard MFA admin operation against a batch of principals via
 * bulkExecuteAuthgardAdminOperation.
 *
 * Returns a map from principalArn to a per-target {@link AuthgardItemResult}: `success` carries the
 * fresh code/status/expiry; `failure` carries the error code; `unknown` marks a requested target the
 * backend returned no result for — an undetermined write outcome (it may have partially applied), so
 * callers treat it as an error. Every requested target is present in the map. The single-target modal
 * reads one entry; a bulk caller can split the map via partitionAuthgardResults.
 *
 * This only sends the operation the admin already chose in the UI. PCS and Authgard do the real
 * authorization and validation server-side, including re-checking the claimed userType against the
 * stored record.
 *
 * @param input the operation, credential type, scope, and targets.
 * @return map of principalArn -> {@link AuthgardItemResult} of the operation's result.
 * @throws if the batch exceeds 50 targets, or the mutation fails at the transport level.
 */
export const executeAuthgardAdminOperation = async (
    input: ExecuteAuthgardAdminOperationInput,
): Promise<Map<string, AuthgardItemResult<AdminOperationSuccess>>> => {
    const resultByArn = new Map<string, AuthgardItemResult<AdminOperationSuccess>>();
    if (input.targets.length === 0) {
        // No targets: nothing to do (PCS also requires at least one). Return an empty map.
        return resultByArn;
    }
    if (input.targets.length > MAX_AUTHGARD_ADMIN_TARGETS_PER_CALL) {
        // Too many targets is a caller bug — split into batches instead of letting PCS reject it.
        throw new Error(`executeAuthgardAdminOperation received ${input.targets.length} targets; the ` +
            `max per call is ${MAX_AUTHGARD_ADMIN_TARGETS_PER_CALL} (AdminOperationTargetList @length in ` +
            `FITPrincipalControlServiceModel).`);
    }
    // Default every requested target to "unknown"; a success or failure entry below overrides it. A
    // target left unknown is one the backend returned no result for — the write may have partially
    // applied, so callers treat it as an error, not a success.
    input.targets.forEach(target => resultByArn.set(target.principalArn, { status: AuthgardResultStatus.UNKNOWN }));

    try {
        logger.info(`Calling bulkExecuteAuthgardAdminOperation: operation=${input.operation}, ` +
            `credentialType=${input.credentialType}, scope=${input.scope}, targets=${input.targets.length}`);
        graphqlClientMetricsPublisher.publishCounterMonitor('bulk-execute-authgard-admin-operation.INVOCATION', 1);

        const result = await getApolloClient().mutate<BulkExecuteAuthgardAdminOperationData, BulkExecuteAuthgardAdminOperationVariables>({
            mutation: BULK_EXECUTE_AUTHGARD_ADMIN_OPERATION_MUTATION,
            variables: {
                bulkExecuteAuthgardAdminOperationInput: {
                    tenantId: TenantID.AFTX,
                    scope: input.scope,
                    targets: input.targets,
                    operation: input.operation,
                    credentialType: input.credentialType,
                },
            },
        });

        const results = result.data?.bulkExecuteAuthgardAdminOperation?.results ?? [];
        for (const entry of results) {
            if (entry.success) {
                resultByArn.set(entry.principalArn, { status: AuthgardResultStatus.SUCCESS, value: entry.success });
            } else if (entry.failure) {
                resultByArn.set(entry.principalArn, {
                    status: AuthgardResultStatus.FAILURE,
                    errorCode: entry.failure.errorCode,
                    errorMessage: entry.failure.errorMessage,
                });
            }
        }

        // One pass groups the results by status; emit a per-reason metric and log the identities in
        // each bucket (with the error results for failures) so failures and (especially)
        // unknown-outcome writes are greppable when debugging.
        const { successes, failures, unknown } = partitionAuthgardResults(resultByArn);
        if (failures.length > 0) {
            graphqlClientMetricsPublisher.publishCounterMonitor('bulk-execute-authgard-admin-operation.OPERATION_FAILURE', failures.length);
            logger.warn(`bulkExecuteAuthgardAdminOperation (operation=${input.operation}): ${failures.length} failure(s) — ` +
                failures.map(f => `${f.principalArn}: ${f.errorCode} (${f.errorMessage})`).join('; '));
        }
        if (unknown.length > 0) {
            // Requested targets the backend never returned a result for — an unknown write outcome
            // (it may have partially applied). Surface loudly so it isn't mistaken for success.
            graphqlClientMetricsPublisher.publishCounterMonitor('bulk-execute-authgard-admin-operation.MISSING_RESULT', unknown.length);
            logger.warn(`bulkExecuteAuthgardAdminOperation (operation=${input.operation}): no result for ${unknown.length} ` +
                `of ${input.targets.length} target(s) — ${unknown.join(', ')} (outcome unknown; may have partially applied)`);
        }
        if (successes.length > 0) {
            graphqlClientMetricsPublisher.publishCounterMonitor('bulk-execute-authgard-admin-operation.OPERATION_SUCCESS', successes.length);
        }
        logger.info(`bulkExecuteAuthgardAdminOperation (operation=${input.operation}) succeeded for ` +
            `${successes.length} of ${input.targets.length} target(s)` +
            (successes.length > 0 ? ` — ${successes.map(s => s.principalArn).join(', ')}` : ''));

        graphqlClientMetricsPublisher.publishCounterMonitor('bulk-execute-authgard-admin-operation.SUCCESS', 1);
        return resultByArn;
    } catch (e) {
        logger.error(`bulkExecuteAuthgardAdminOperation (operation=${input.operation}) failed`, e as Error);
        graphqlClientMetricsPublisher.publishCounterMonitor('bulk-execute-authgard-admin-operation.ERROR', 1);
        throw e;
    }
};
